import { SingleUnit } from './equipmentTypes';
import { tGroupedEquipment } from '../../types';
import { PENDING_NEW_CATEGORY_ID } from './equipmentConstants';

export interface UnitNameConflict {
    unitId: string;
    unitName: string;
    categoryName: string;
}

export type UnitNameDuplicateError = {
    type: 'duplicate';
    enteredName: string;
    categoryName: string;
    suggestedName: string;
};

export type UnitNameRequiredError = {
    type: 'required';
    message: string;
};

export type UnitFieldUniqueError = {
    type: 'unique';
    message: string;
};

export type UnitNameValidationError = UnitNameDuplicateError | UnitNameRequiredError | UnitFieldUniqueError;

type UniqueCheckUnit = {
    id: string;
    name?: string | null;
    categoryId?: string | null;
    category?: { id?: string | null; name?: string | null } | null;
    serialNumber?: string | null;
    engineSerialNumber?: string | null;
    licenseNumber?: string | null;
};

/** Trim only — preserves company-chosen casing for display and storage. */
export const trimUnitName = (name: string): string => name.trim();

/** Case-insensitive key for duplicate checks (EX-001 and ex-001 collide). */
export const normalizeUnitNameKey = (name: string): string => trimUnitName(name).toLowerCase();

export const isPlaceholderUnitName = (name: string): boolean => {
    const trimmed = trimUnitName(name);
    return !trimmed || trimmed === 'New Unit';
};

const buildTakenNameKeys = (units: SingleUnit[], excludeUnitId?: string): Set<string> => {
    const taken = new Set<string>();
    for (const unit of units) {
        if (!unit.name) continue;
        if (excludeUnitId && unit.id === excludeUnitId) continue;
        taken.add(normalizeUnitNameKey(unit.name));
    }
    return taken;
};

const isNameAvailable = (candidate: string, taken: Set<string>): boolean =>
    !taken.has(normalizeUnitNameKey(candidate));

/** Suggest a non-duplicate name based on the entered value (not necessarily sequential). */
export const suggestAvailableUnitName = (
    desiredName: string,
    units: SingleUnit[],
    excludeUnitId?: string,
): string => {
    const base = trimUnitName(desiredName);
    const taken = buildTakenNameKeys(units, excludeUnitId);

    const prefixCodeMatch = base.match(/^([A-Za-z]+-)(\d+)(.*)$/);
    if (prefixCodeMatch) {
        const [, prefix, numStr, rest] = prefixCodeMatch;
        let n = parseInt(numStr, 10);
        for (let i = 0; i < 100; i++) {
            n++;
            const candidate = `${prefix}${String(n).padStart(numStr.length, '0')}${rest}`;
            if (isNameAvailable(candidate, taken)) return candidate;
        }
    }

    for (let i = 2; i <= 99; i++) {
        const candidate = `${base} (${i})`;
        if (isNameAvailable(candidate, taken)) return candidate;
    }

    const trailingNumMatch = base.match(/^(.*?)(\d+)$/);
    if (trailingNumMatch && !prefixCodeMatch) {
        const [, prefix, numStr] = trailingNumMatch;
        let n = parseInt(numStr, 10);
        for (let i = 0; i < 100; i++) {
            n++;
            const candidate = `${prefix}${String(n).padStart(numStr.length, '0')}`;
            if (isNameAvailable(candidate, taken)) return candidate;
        }
    }

    for (let i = 2; i <= 99; i++) {
        const candidate = `${base} - ${i}`;
        if (isNameAvailable(candidate, taken)) return candidate;
    }

    return `${base} (${Date.now() % 100000})`;
};

export const getUnitNameValidationMessage = (error: UnitNameValidationError | null): string | null => {
    if (!error) return null;
    if (error.type === 'required' || error.type === 'unique') return error.message;
    return `"${error.enteredName}" is already used by ${error.categoryName}.`;
};

export const findUnitNameConflict = (
    name: string,
    units: SingleUnit[],
    categories: tGroupedEquipment[],
    excludeUnitId?: string,
): UnitNameConflict | null => {
    const nameKey = normalizeUnitNameKey(name);
    if (!nameKey) return null;

    const categoryNameById = new Map(categories.map((category) => [category.id, category.name]));

    const match = units.find((unit) => {
        if (!unit.name) return false;
        if (excludeUnitId && unit.id === excludeUnitId) return false;
        return normalizeUnitNameKey(unit.name) === nameKey;
    });

    if (!match?.name) return null;

    const categoryId = match.categoryId || match.category?.id;
    const categoryName =
        (categoryId && categoryNameById.get(categoryId)) || match.category?.name || 'Unknown category';

    return {
        unitId: match.id,
        unitName: match.name,
        categoryName,
    };
};

export const validateEquipmentCategoryId = (categoryId: string | undefined | null): UnitNameValidationError | null => {
    if (!categoryId?.trim()) {
        return { type: 'required', message: 'Equipment Category is required.' };
    }

    return null;
};

export const isPendingNewCategoryId = (categoryId?: string | null): boolean =>
    categoryId === PENDING_NEW_CATEGORY_ID;

export const findCategoryNameConflict = (
    name: string,
    categories: tGroupedEquipment[],
    excludeCategoryId?: string | null,
): string | null => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    const key = trimmed.toLowerCase();
    const match = categories.find((category) => {
        if (excludeCategoryId && category.id === excludeCategoryId) return false;
        return category.name.trim().toLowerCase() === key;
    });
    return match?.name ?? null;
};

export const validateEquipmentCategorySelection = (
    categoryId: string | undefined | null,
    pendingCategoryName: string | null | undefined,
    categories: tGroupedEquipment[],
): UnitNameValidationError | null => {
    if (!categoryId?.trim()) {
        return { type: 'required', message: 'Equipment Category is required.' };
    }

    if (!isPendingNewCategoryId(categoryId)) {
        return null;
    }

    const name = pendingCategoryName?.trim() || '';
    if (!name) {
        return { type: 'required', message: 'Equipment Category is required.' };
    }

    const existingName = findCategoryNameConflict(name, categories);
    if (existingName) {
        return {
            type: 'required',
            message: `Equipment category "${existingName}" already exists. Select it from the list instead.`,
        };
    }

    return null;
};

/** Case-insensitive uniqueness check for create/rename equipment category flows. */
export const getEquipmentCategoryNameConflictMessage = (
    name: string,
    categories: tGroupedEquipment[],
    excludeCategoryId?: string | null,
): string | null => {
    const existingName = findCategoryNameConflict(name, categories, excludeCategoryId);
    if (!existingName) return null;
    return `A category named “${existingName}” already exists. Category names must be unique.`;
};

export const validateUnitName = (
    name: string,
    units: SingleUnit[],
    categories: tGroupedEquipment[],
    excludeUnitId?: string,
): UnitNameValidationError | null => {
    if (isPlaceholderUnitName(name)) {
        return { type: 'required', message: 'Unit Name / ID is required.' };
    }

    const conflict = findUnitNameConflict(name, units, categories, excludeUnitId);
    if (conflict) {
        const displayName = trimUnitName(name);
        return {
            type: 'duplicate',
            enteredName: displayName,
            categoryName: conflict.categoryName,
            suggestedName: suggestAvailableUnitName(displayName, units, excludeUnitId),
        };
    }

    return null;
};

const getUnitFieldValue = (
    unit: UniqueCheckUnit,
    field: 'serialNumber' | 'licenseNumber',
): string => (unit[field] ?? '').trim();

export const findUnitFieldConflict = (
    value: string,
    field: 'serialNumber' | 'licenseNumber',
    units: UniqueCheckUnit[],
    categories: Array<{ id: string; name: string }>,
    excludeUnitId?: string,
): UnitNameConflict | null => {
    const valueKey = normalizeUnitNameKey(value);
    if (!valueKey) return null;

    const categoryNameById = new Map(categories.map((category) => [category.id, category.name]));

    const match = units.find((unit) => {
        if (excludeUnitId && unit.id === excludeUnitId) return false;
        return normalizeUnitNameKey(getUnitFieldValue(unit, field)) === valueKey;
    });

    if (!match) return null;

    const categoryId = match.categoryId || match.category?.id;
    const categoryName =
        (categoryId && categoryNameById.get(categoryId)) || match.category?.name || 'Unknown category';

    return {
        unitId: match.id,
        unitName: match.name || 'Untitled unit',
        categoryName,
    };
};

export const findSerialIdentifierConflict = (
    value: string,
    units: UniqueCheckUnit[],
    categories: Array<{ id: string; name: string }>,
    excludeUnitId?: string,
    ignoreFieldOnExcludedUnit?: 'serialNumber' | 'engineSerialNumber',
): UnitNameConflict | null => {
    const valueKey = normalizeUnitNameKey(value);
    if (!valueKey) return null;

    const categoryNameById = new Map(categories.map((category) => [category.id, category.name]));

    const match = units.find((unit) => {
        const serialKey = normalizeUnitNameKey(unit.serialNumber ?? '');
        const engineKey = normalizeUnitNameKey(unit.engineSerialNumber ?? '');
        if (excludeUnitId && unit.id === excludeUnitId) {
            if (ignoreFieldOnExcludedUnit === 'serialNumber') return engineKey === valueKey;
            if (ignoreFieldOnExcludedUnit === 'engineSerialNumber') return serialKey === valueKey;
            return false;
        }
        return serialKey === valueKey || engineKey === valueKey;
    });

    if (!match) return null;

    const categoryId = match.categoryId || match.category?.id;
    const categoryName =
        (categoryId && categoryNameById.get(categoryId)) || match.category?.name || 'Unknown category';

    return {
        unitId: match.id,
        unitName: match.name || 'Untitled unit',
        categoryName,
    };
};

export const validateUniqueOptionalUnitField = (
    value: string,
    field: 'serialNumber' | 'licenseNumber',
    fieldLabel: string,
    units: UniqueCheckUnit[],
    categories: Array<{ id: string; name: string }>,
    excludeUnitId?: string,
): UnitNameValidationError | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const conflict = findUnitFieldConflict(trimmed, field, units, categories, excludeUnitId);
    if (!conflict) return null;

    return {
        type: 'unique',
        message: `${fieldLabel} "${trimmed}" is already used by ${conflict.unitName} (${conflict.categoryName}).`,
    };
};

export const validateSerialNumber = (
    value: string,
    units: UniqueCheckUnit[],
    categories: Array<{ id: string; name: string }>,
    excludeUnitId?: string,
): UnitNameValidationError | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const conflict = findSerialIdentifierConflict(
        trimmed,
        units,
        categories,
        excludeUnitId,
        'serialNumber',
    );
    if (!conflict) return null;
    if (excludeUnitId && conflict.unitId === excludeUnitId) {
        return {
            type: 'unique',
            message: 'Unit Serial # and Engine Serial # on this unit cannot be the same.',
        };
    }
    return {
        type: 'unique',
        message: `Unit Serial # "${trimmed}" is already used by ${conflict.unitName} (${conflict.categoryName}).`,
    };
};

export const validateEngineSerialNumber = (
    value: string,
    units: UniqueCheckUnit[],
    categories: Array<{ id: string; name: string }>,
    excludeUnitId?: string,
): UnitNameValidationError | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const conflict = findSerialIdentifierConflict(
        trimmed,
        units,
        categories,
        excludeUnitId,
        'engineSerialNumber',
    );
    if (!conflict) return null;
    if (excludeUnitId && conflict.unitId === excludeUnitId) {
        return {
            type: 'unique',
            message: 'Unit Serial # and Engine Serial # on this unit cannot be the same.',
        };
    }
    return {
        type: 'unique',
        message: `Engine Serial # "${trimmed}" is already used by ${conflict.unitName} (${conflict.categoryName}).`,
    };
};

export const validateLicenseNumber = (
    value: string,
    units: UniqueCheckUnit[],
    categories: Array<{ id: string; name: string }>,
    excludeUnitId?: string,
): UnitNameValidationError | null =>
    validateUniqueOptionalUnitField(value, 'licenseNumber', 'License #', units, categories, excludeUnitId);

/** @deprecated Use validateUnitName without excludeUnitId */
export const validateNewUnitName = (
    name: string,
    units: SingleUnit[],
    categories: tGroupedEquipment[],
): UnitNameValidationError | null => validateUnitName(name, units, categories);
